import { Dispatch, SetStateAction, useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { FormikProps, useFormikContext, FieldArray } from 'formik'
import { Options } from 'react-select'
import { mdiPlus, mdiDeleteOutline, mdiCheck, mdiClose } from '@mdi/js'
import path from 'lodash/fp/path'
import _uniqBy from 'lodash/uniqBy'
import _uniq from 'lodash/uniq'
import Icon from '@mdi/react'
import { mdiAlert } from '@mdi/js'

import { DropdownOption } from 'components/Input/Dropdown'
import { SpinnerCentered } from 'components/Loader'
import { commonMessages } from 'app/i18n/messages'
import { BodyPrimary } from 'components/Typography'
import { hasSystemAccessEnabled } from 'features/home/utils'
import { useAppSelector } from 'app/hooks'
import { selectedHomeSelector } from 'features/home/selectors'
import theme from 'theme_'

import type { HomeServicesEvent } from '../../types'
import { DropdownField, TextField } from 'components/Fields'
import { SERVICES_EVENTS_FORM } from '../../consts'
import { homeServicesEventsMessages } from '../messages'
import useServiceEvents from '../../hooks'
import { ModalControlContext } from '../../modalControlContext'
import {
  AddArgumentButton,
  DeleteArgumentButton,
  HomeServicesIcon,
  HomeServicesEventsArgumentsBody,
  HomeServicesEventsArgumentsHeader,
  HomeServicesEventsArgumentsWrapper,
  HomeServicesEventsResetButton,
  HomeServicesEventsDropdownsContainer,
  HomeServicesEventsDropdownWrapper,
  HomeServicesEventsFormContainer,
  HomeServicesEventsFormBottomContainer,
  HomeServicesEventsFormButtons,
  HomeServicesEventsFormInfo,
  HomeServicesEventsFormTopContainer,
  HomeServicesEventsSubmitButtonWrapper,
  HomeServicesEventsSubmitButton,
  HomeServicesHr,
  HomeServicesEventsIcon,
  HomeServicesEventsSubmitSuccessWrapper,
  HomeServicesEventsSubmitSuccess,
  HomeServicesEventsTitle,
  HomeServicesEventsArgumentsInputsWrapper,
  HomeServicesEventsResetButtonMobileLabel,
  HomeServicesEventsResetButtonDesktopLabel,
  HomeServicesMissingAccess,
  HomeServicesEventsFormCloseBtn,
} from './styles'

interface HomeServicesEventsFormProps {
  formikProps: FormikProps<HomeServicesEvent>
  selectedTableService?: HomeServicesEvent
  setZoneFilter: Dispatch<SetStateAction<DropdownOption | undefined>>
  setServiceTypeFilter: Dispatch<SetStateAction<DropdownOption | undefined>>
  setServiceIdFilter: Dispatch<SetStateAction<DropdownOption | undefined>>
  zonesDropdownOptions: Options<DropdownOption>
  servicesDropdownOptions: Options<DropdownOption>
  typesDropdownOptions: Options<DropdownOption>
}

function HomeServicesEventsForm({
  formikProps,
  selectedTableService,
  setZoneFilter,
  setServiceTypeFilter,
  setServiceIdFilter,
  zonesDropdownOptions,
  servicesDropdownOptions,
  typesDropdownOptions,
}: HomeServicesEventsFormProps) {
  const { formatMessage } = useIntl()
  const modalControlContext = useContext(ModalControlContext)
  const home = useAppSelector(selectedHomeSelector.data)
  const hasSystemAccess = hasSystemAccessEnabled(home)

  const { values, setFieldValue, isSubmitting, status } =
    useFormikContext<HomeServicesEvent>()
  // TODO: implement the support of ALL TYPES option, currently its not working
  const { serviceEventsList, setServiceEventsList, error } = useServiceEvents(
    values.serviceType
  )

  const handleReset = () => {
    formikProps.resetForm()
    setZoneFilter(undefined)
    setServiceTypeFilter(undefined)
    setServiceIdFilter(undefined)
    setServiceEventsList(null)
    if (modalControlContext.handleModalClose) {
      modalControlContext.handleModalClose()
    }
  }

  const eventsDropdownOptions = useMemo(
    () =>
      _uniqBy(
        serviceEventsList?.requestIds
          .sort()
          .map((item) => ({ label: item, value: item })),
        'value'
      ),
    [serviceEventsList?.requestIds]
  )

  const disableSubmit =
    formikProps.values.event === undefined ||
    formikProps.values.zone === undefined ||
    formikProps.values.serviceId === undefined ||
    formikProps.values.serviceType === undefined ||
    !Array.isArray(formikProps.values.arguments)

  const eventArguments = useMemo(
    () =>
      values.event ? _uniq(serviceEventsList?.requests[values.event]) : [],
    [serviceEventsList?.requests, values.event]
  )

  useEffect(() => {
    // .concat(['']) - adding an argument with empty fields, based on UX design
    setFieldValue(
      'arguments',
      eventArguments.concat(['']).map((item) => ({ name: item, value: '' }))
    )
  }, [eventArguments, setFieldValue])

  // fulfills form based on a selected service in a table
  useEffect(() => {
    setFieldValue(SERVICES_EVENTS_FORM.ZONE, selectedTableService?.zone)
    setFieldValue(
      SERVICES_EVENTS_FORM.SERVICE_ID,
      selectedTableService?.serviceId
    )
    setFieldValue(
      SERVICES_EVENTS_FORM.SERVICE_TYPE,
      selectedTableService?.serviceType
    )
    setFieldValue(SERVICES_EVENTS_FORM.EVENT, undefined)
  }, [selectedTableService, setFieldValue])

  return (
    <HomeServicesEventsFormContainer>
      <HomeServicesEventsFormTopContainer>
        <HomeServicesEventsFormCloseBtn onClick={() => handleReset()}>
          <Icon path={mdiClose} size={1} color={theme.colors.horizonOrange} />
        </HomeServicesEventsFormCloseBtn>
        <HomeServicesEventsTitle>
          {formatMessage(homeServicesEventsMessages.formHeading)}
        </HomeServicesEventsTitle>
        <HomeServicesEventsDropdownsContainer>
          <HomeServicesEventsDropdownWrapper>
            <DropdownField
              name={SERVICES_EVENTS_FORM.ZONE}
              placeholder={formatMessage(
                homeServicesEventsMessages.zonePlaceholder
              )}
              options={zonesDropdownOptions}
              isSearchable
              showLabel
              handleChange={(option: DropdownOption) => setZoneFilter(option)}
            />
          </HomeServicesEventsDropdownWrapper>
          <HomeServicesEventsDropdownWrapper>
            <DropdownField
              name={SERVICES_EVENTS_FORM.SERVICE_TYPE}
              placeholder={formatMessage(
                homeServicesEventsMessages.typePlaceholder
              )}
              options={typesDropdownOptions}
              isSearchable
              showLabel
              handleChange={(option: DropdownOption) =>
                setServiceTypeFilter(option)
              }
            />
          </HomeServicesEventsDropdownWrapper>
          <HomeServicesEventsDropdownWrapper>
            <DropdownField
              name={SERVICES_EVENTS_FORM.SERVICE_ID}
              placeholder={formatMessage(
                homeServicesEventsMessages.servicePlaceholder
              )}
              options={servicesDropdownOptions}
              showLabel
              isSearchable
              handleChange={(option: DropdownOption) =>
                setServiceIdFilter(option)
              }
            />
          </HomeServicesEventsDropdownWrapper>
        </HomeServicesEventsDropdownsContainer>

        <HomeServicesHr />

        {error && (
          <BodyPrimary>{formatMessage(commonMessages.commonError)}</BodyPrimary>
        )}

        {serviceEventsList?.requestIds && (
          <>
            <HomeServicesEventsDropdownWrapper>
              <DropdownField
                name={SERVICES_EVENTS_FORM.EVENT}
                placeholder={formatMessage(
                  homeServicesEventsMessages.eventPlaceholder
                )}
                options={eventsDropdownOptions}
                showLabel
                isSearchable
              />
            </HomeServicesEventsDropdownWrapper>

            <HomeServicesEventsArgumentsWrapper>
              <HomeServicesEventsArgumentsHeader>
                {formatMessage(homeServicesEventsMessages.argumentsHeader)}
              </HomeServicesEventsArgumentsHeader>
              <FieldArray name="arguments">
                {({ remove, push }) => (
                  <>
                    {path(['values', 'arguments'], formikProps)?.map(
                      (arg, index) => (
                        <HomeServicesEventsArgumentsBody key={index}>
                          <HomeServicesEventsArgumentsInputsWrapper>
                            <TextField
                              maxLength={256}
                              name={`arguments.${index}.name`}
                              placeholder={formatMessage(
                                homeServicesEventsMessages.argumentsNamePlaceholder
                              )}
                            />
                            <TextField
                              maxLength={256}
                              name={`arguments.${index}.value`}
                              placeholder={formatMessage(
                                homeServicesEventsMessages.argumentsValuePlaceholder
                              )}
                            />
                          </HomeServicesEventsArgumentsInputsWrapper>
                          <DeleteArgumentButton
                            type="button"
                            onClick={() => remove(index)}
                          >
                            <HomeServicesIcon
                              path={mdiDeleteOutline}
                              size={1}
                            />
                          </DeleteArgumentButton>
                        </HomeServicesEventsArgumentsBody>
                      )
                    )}
                    <AddArgumentButton
                      type="button"
                      onClick={() => push({ name: '', value: '' })}
                    >
                      <HomeServicesIcon path={mdiPlus} size={1} />
                      {formatMessage(
                        homeServicesEventsMessages.addArgumentButton
                      )}
                    </AddArgumentButton>
                  </>
                )}
              </FieldArray>
            </HomeServicesEventsArgumentsWrapper>
          </>
        )}
      </HomeServicesEventsFormTopContainer>

      <HomeServicesEventsFormBottomContainer>
        <HomeServicesEventsFormInfo>
          {hasSystemAccess ? (
            <>{status?.error && formatMessage(commonMessages.commonError)}</>
          ) : (
            <HomeServicesMissingAccess>
              <Icon
                path={mdiAlert}
                size={0.9}
                color={theme.colors.horizonOrange}
              />
              <span>Request home configuration access</span>
            </HomeServicesMissingAccess>
          )}
        </HomeServicesEventsFormInfo>

        <HomeServicesEventsFormButtons>
          {hasSystemAccess ? (
            <HomeServicesEventsResetButton
              variant="secondary"
              type="button"
              onClick={handleReset}
            >
              <HomeServicesEventsResetButtonMobileLabel>
                {formatMessage(commonMessages.cancel)}
              </HomeServicesEventsResetButtonMobileLabel>
              <HomeServicesEventsResetButtonDesktopLabel>
                {formatMessage(homeServicesEventsMessages.resetButton)}
              </HomeServicesEventsResetButtonDesktopLabel>
            </HomeServicesEventsResetButton>
          ) : (
            <span />
          )}

          <HomeServicesEventsSubmitButtonWrapper>
            {status?.success && (
              <HomeServicesEventsSubmitSuccessWrapper>
                <HomeServicesEventsIcon path={mdiCheck} size={0.8} />
                <HomeServicesEventsSubmitSuccess>
                  {formatMessage(commonMessages.success)}
                </HomeServicesEventsSubmitSuccess>
              </HomeServicesEventsSubmitSuccessWrapper>
            )}

            {isSubmitting && (
              <HomeServicesEventsSubmitSuccessWrapper>
                <SpinnerCentered size="40" />
              </HomeServicesEventsSubmitSuccessWrapper>
            )}

            {!isSubmitting && !status?.success && (
              <HomeServicesEventsSubmitButton
                variant="primary"
                type="submit"
                disabled={disableSubmit || status?.error || !hasSystemAccess}
              >
                {formatMessage(homeServicesEventsMessages.sendEventButton)}
              </HomeServicesEventsSubmitButton>
            )}
          </HomeServicesEventsSubmitButtonWrapper>
        </HomeServicesEventsFormButtons>
      </HomeServicesEventsFormBottomContainer>
    </HomeServicesEventsFormContainer>
  )
}

export default HomeServicesEventsForm
